
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageOpen(false);
  };

  const currentLanguage = i18n.language === 'pt' ? 'PT' : 'EN';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <div className="flex flex-col">
            <Link to="/" className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors">
              PEDRO FAERSTEIN
            </Link>
            <h1 className="text-sm font-light tracking-wider text-gray-800 uppercase">
              {t('cinematographer')}
            </h1>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex items-center space-x-8">
              <Link 
                to="/" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('home')}
              </Link>
              <Link 
                to="/narrative" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('narrative')}
              </Link>
              <Link 
                to="/commercial" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('commercial')}
              </Link>
              <Link 
                to="/doc" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('doc')}
              </Link>
              <Link 
                to="/reel" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('reel')}
              </Link>
              <Link 
                to="/bio" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('bio')}
              </Link>
              <Link 
                to="/contact" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
              >
                {t('contact')}
              </Link>
            </nav>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-1 text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors cursor-pointer"
              >
                <span>{currentLanguage}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLanguageOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-100 rounded-sm shadow-lg py-2 min-w-[120px]">
                  <button
                    onClick={() => changeLanguage('en')}
                    className="w-full px-4 py-2 text-left text-sm font-light tracking-wider text-gray-800 uppercase hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    {t('english')}
                  </button>
                  <button
                    onClick={() => changeLanguage('pt')}
                    className="w-full px-4 py-2 text-left text-sm font-light tracking-wider text-gray-800 uppercase hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    {t('portuguese')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-800 hover:text-gray-600 transition-colors"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-gray-100 pt-4">
            <div className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('home')}
              </Link>
              <Link 
                to="/narrative" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('narrative')}
              </Link>
              <Link 
                to="/commercial" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('commercial')}
              </Link>
              <Link 
                to="/doc" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('doc')}
              </Link>
              <Link 
                to="/reel" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('reel')}
              </Link>
              <Link 
                to="/bio" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('bio')}
              </Link>
              <Link 
                to="/contact" 
                className="text-sm font-light tracking-wider text-gray-800 uppercase hover:text-gray-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('contact')}
              </Link>
              
              {/* Mobile Language Switcher */}
              <div className="pt-2 border-t border-gray-100">
                <div className="text-xs font-light tracking-wider text-gray-600 uppercase mb-2">
                  {t('language')}
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      changeLanguage('en');
                      setIsMenuOpen(false);
                    }}
                    className={`text-sm font-light tracking-wider uppercase transition-colors cursor-pointer ${
                      i18n.language === 'en' ? 'text-gray-800' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('english')}
                  </button>
                  <button
                    onClick={() => {
                      changeLanguage('pt');
                      setIsMenuOpen(false);
                    }}
                    className={`text-sm font-light tracking-wider uppercase transition-colors cursor-pointer ${
                      i18n.language === 'pt' ? 'text-gray-800' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('portuguese')}
                  </button>
                </div>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
